/**
 * The recording lane (board notes, „Layout"): × to cancel, the dot and the timer, the waveform. It
 * stands in the composer's own row over the controls it replaces, so the box never changes size;
 * hidden, it is inert and transparent, never unmounted, so its entrance and exit are one gesture.
 */

import clsx from 'clsx';
import { useAnimationFrame } from 'motion/react';
import { useRef, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseGlyph } from '~/shared/ui/FormGlyphs';

import { COUNTDOWN_SECONDS, clockOf, laneSaysTranscribing, secondsLeft, waveMode } from '../model/dictation';
import type { Dictation } from '../lib/useDictation';
import { DictationWave } from './DictationWave';
import surfaces from './surfaces.module.css';
import styles from './Composer.module.css';

export interface DictationLaneProps {
  readonly layout: 'hero' | 'dock';
  readonly dictation: Dictation;
  readonly reducedMotion: boolean;
  readonly onCancel: () => void;
  readonly cancelRef: Ref<HTMLButtonElement>;
}

export function DictationLane({ layout, dictation, reducedMotion, onCancel, cancelRef }: DictationLaneProps) {
  const { t } = useTranslation();
  const dot = useRef<HTMLSpanElement>(null);
  const { phase } = dictation.state;
  const shown = phase !== 'idle';
  const left = phase === 'recording' ? secondsLeft(dictation.elapsed, dictation.maxSeconds) : null;
  const transcribing = phase === 'transcribing';
  const saysTranscribing = laneSaysTranscribing(layout, phase);

  return (
    <div className={clsx(styles.lane, styles.fades, !shown && styles.away, layout === 'dock' && styles.cellLane)} inert={!shown}>
      <button ref={cancelRef} type="button" className={clsx(styles.icon, styles.iconLive)} aria-label={t('web:aiPlan.dictation.cancel')} onClick={onCancel}>
        <CloseGlyph className={styles.iconGlyph} />
      </button>
      <span
        className={clsx(styles.timer, left !== null && styles.timerWarn, (phase === 'requesting' || (transcribing && !saysTranscribing)) && styles.timerDim)}
        aria-hidden="true"
      >
        {!transcribing && <span ref={dot} className={styles.dot} />}
        {saysTranscribing ? <span className={surfaces.shimmer}>{t('web:aiPlan.dictation.transcribingLane')}</span> : clockOf(dictation.elapsed)}
      </span>
      {phase !== 'idle' && (
        <DictationWave
          mode={waveMode(phase)}
          readLevel={dictation.readLevel}
          reducedMotion={reducedMotion}
          dot={dot}
        />
      )}
    </div>
  );
}

const ARC_LENGTH = 2 * Math.PI * 23.5;

/** The thin countdown round the ✓, closing over the last ten seconds, moved every frame. */
export function CountdownArc({ dictation }: { readonly dictation: Dictation }) {
  const on = dictation.state.phase === 'recording' && secondsLeft(dictation.elapsed, dictation.maxSeconds) !== null;
  return (
    <svg className={clsx(styles.arc, on && styles.arcOn)} viewBox="0 0 50 50" aria-hidden="true" focusable="false">
      {on ? <ClosingCircle dictation={dictation} /> : <circle cx="25" cy="25" r="23.5" strokeDasharray={ARC_LENGTH} strokeDashoffset={ARC_LENGTH} />}
    </svg>
  );
}

/** The arc while it closes: its frame loop runs only while the countdown does. */
function ClosingCircle({ dictation }: { readonly dictation: Dictation }) {
  const circle = useRef<SVGCircleElement>(null);
  useAnimationFrame(() => {
    if (circle.current === null) return;
    const remaining = Math.max(0, dictation.maxSeconds - dictation.readElapsedMs() / 1_000);
    circle.current.style.strokeDashoffset = String(ARC_LENGTH * (1 - Math.min(1, remaining / COUNTDOWN_SECONDS)));
  });
  return <circle ref={circle} cx="25" cy="25" r="23.5" strokeDasharray={ARC_LENGTH} strokeDashoffset={0} />;
}
